import { SxProps, Typography } from '@mui/material';
import { HTMLReactParserOptions } from 'html-react-parser';
import useTranslation from 'next-translate/useTranslation';
import { FC, ReactNode } from 'react';
import DisplayFullText from './DisplayFullText';
import FullTextSkeleton from './Skeleton';
import { DocumentSearchTarget } from '@models/searchable';
import useFullText from '@utils/hooks/useFullText';
import { SanitizeOptions } from '@utils/parsing';

export { FullTextSkeleton };

interface FullTextWrapperProps {
    collectionName: string;
    containerSx?: SxProps;
    documentId: string;
    parseOptions?: HTMLReactParserOptions;
    refetchOnChange?: boolean;
    removeAfterFetch?: boolean;
    retryOnFail?: boolean;
    sanitizeOptions?: SanitizeOptions;
    searchControls?: ReactNode;
    searchTarget?: DocumentSearchTarget;
    searchTargetId?: string;
    withSearch?: boolean;
}

const FullTextWrapper: FC<FullTextWrapperProps> = ({
    collectionName,
    containerSx,
    documentId,
    parseOptions,
    refetchOnChange,
    removeAfterFetch,
    retryOnFail = false,
    sanitizeOptions,
    searchControls,
    searchTarget,
    searchTargetId,
    withSearch,
}) => {
    const { t } = useTranslation('common');
    const [fullText, loading, error] = useFullText(
        documentId,
        collectionName,
        retryOnFail,
        refetchOnChange,
        removeAfterFetch,
    );

    if (loading) {
        return (
            <>
                <FullTextSkeleton lines={6} withHeader />
                <FullTextSkeleton lines={4} sx={{ mt: 2 }} />
                <FullTextSkeleton lines={7} sx={{ mt: 2 }} />
            </>
        );
    }
    if (error) {
        return <Typography>{t('error.fullText')}</Typography>;
    }

    return (
        <DisplayFullText
            containerSx={containerSx}
            parseOptions={parseOptions}
            sanitizeOptions={sanitizeOptions}
            searchControls={searchControls}
            searchTarget={searchTarget}
            searchTargetId={searchTargetId}
            unsanitizedContent={fullText ?? ''}
            withSearch={withSearch}
        />
    );
};

export default FullTextWrapper;
